import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  CmrSubscription,
  CmrSubscriptionResponse,
  CmrSubscriptionListResponse,
  CmrSubscriptionType,
} from '../models/cmr-subscription.model';

@Injectable({ providedIn: 'root' })
export class CmrSubscriptionService {
  private apiUrl = `${environment.glassApiUrl}/v2/subscriptions`;

  private _subscriptions$ = new BehaviorSubject<CmrSubscription[]>([]);
  readonly subscriptions$ = this._subscriptions$.asObservable();

  /** Emits whenever loading the user's subscriptions fails (transport error or `success: false`). */
  private _loadError$ = new Subject<void>();
  readonly loadError$ = this._loadError$.asObservable();

  private _loading = false;

  get snapshot(): CmrSubscription[] { return this._subscriptions$.value; }

  constructor(private http: HttpClient) {}

  load(email: string): void {
    if (this._loading) return;
    this._loading = true;
    this.http.get<CmrSubscriptionListResponse>(`${this.apiUrl}?email=${encodeURIComponent(email)}`)
      .subscribe({
        next: (res) => {
          if (res.success) this._subscriptions$.next(res.result ?? []);
          else this._loadError$.next();
          this._loading = false;
        },
        error: () => {
          this._loadError$.next();
          this._loading = false;
        },
      });
  }

  subscribe(userEmail: string, type: CmrSubscriptionType, targetId: string, targetLabel: string): Observable<CmrSubscriptionResponse> {
    return this.http.post<CmrSubscriptionResponse>(this.apiUrl, { userEmail, type, targetId, targetLabel }).pipe(
      tap((res) => {
        if (res.success && res.result) {
          this._subscriptions$.next([...this.snapshot, res.result]);
        }
      })
    );
  }

  unsubscribe(id: string, userEmail: string): Observable<CmrSubscriptionResponse> {
    return this.http.delete<CmrSubscriptionResponse>(`${this.apiUrl}/${id}?email=${encodeURIComponent(userEmail)}`).pipe(
      tap((res) => {
        if (res.success) {
          this._subscriptions$.next(this.snapshot.filter(s => s._id !== id));
        }
      })
    );
  }

  getByTarget(type: CmrSubscriptionType, targetId: string): Observable<CmrSubscriptionListResponse> {
    return this.http.get<CmrSubscriptionListResponse>(
      `${this.apiUrl}/by-target?type=${encodeURIComponent(type)}&targetId=${encodeURIComponent(targetId)}`
    );
  }
}
